import { FunctionComponent, useEffect, useState } from 'react'
import {
  Box, Typography, TextField, Button, Card, CardContent, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
  Select, MenuItem, InputLabel, FormControl, Table, TableHead, TableBody,
  TableRow, TableCell, Chip,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/styles'
import { AlertTriangle } from 'react-feather'
import { toast } from 'react-toastify'
import { useNavigate } from 'react-router-dom'
import axios from '../../utils/axios'
import colors from '../../colors'
import { SPACING } from '../../constants'
import useMembershipsStore from '../../stores/memberships'

/**
 * SMS composer. Separate from the wallet composer on purpose: the constraints
 * do not overlap. A wallet push caps at 150 characters and costs nothing; a
 * text bills per segment and only reaches members who affirmatively agreed to
 * receive marketing.
 */

// Three 160-character segments. Twilio bills per segment, and any emoji
// forces UCS-2 and drops the limit to 70 — so this is a cost ceiling, not a
// character count a brewery would recognise.
const MAX_BODY_LENGTH = 480
const SEGMENT_LENGTH = 160

type TargetType = 'all_members' | 'recent_signups' | 'membership'

interface Broadcast {
  id: number
  body: string
  targetType: TargetType
  targetDescription: string
  state: 'draft' | 'sending' | 'sent' | 'failed'
  sentAt: string | null
  createdAt: string
  sentBy: string | null
  recipientCount: number
  excludedNoPhone: number
  excludedUnsubscribed: number
  excludedNoConsent: number
  deliveredCount: number
  failedCount: number
}

interface Balance {
  textsRemaining: number
  includedPerMonth: number | null
  topUpAmountCents: number
  topUpTexts: number
}

interface Breakdown {
  total: number
  sendable: number
  noPhone: number
  unsubscribed: number
  noConsent: number
}

const SmsBroadcasts: FunctionComponent = () => {
  const styles = useStyles()
  const navigate = useNavigate()
  const memberships = useMembershipsStore((state) => state.memberships)
  const getMemberships = useMembershipsStore((state) => state.getMemberships)

  const [body, setBody] = useState('')
  const [targetType, setTargetType] = useState<TargetType>('all_members')
  const [membershipId, setMembershipId] = useState('')

  const [balance, setBalance] = useState<Balance | null>(null)
  const [sendable, setSendable] = useState(false)
  const [unsendableReason, setUnsendableReason] = useState<string | null>(null)
  const [broadcasts, setBroadcasts] = useState<Broadcast[]>([])
  const [loading, setLoading] = useState(true)

  const [breakdown, setBreakdown] = useState<Breakdown | null>(null)
  const [previewing, setPreviewing] = useState(false)
  const [sending, setSending] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)

  useEffect(() => {
    getMemberships()
    fetchHistory()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Any change to the audience or body invalidates the preview — the counts
  // shown must always describe what is about to be sent.
  useEffect(() => {
    setBreakdown(null)
  }, [body, targetType, membershipId])

  const fetchHistory = async () => {
    setLoading(true)
    try {
      const res = await axios.get('/sms_broadcasts')
      setSendable(res.data.sendable)
      setBalance(res.data.balance)
      setUnsendableReason(res.data.unsendableReason)
      setBroadcasts(res.data.broadcasts)
    } catch {
      toast.error('Failed to load text message history')
    } finally {
      setLoading(false)
    }
  }

  const buildParams = () => {
    const params: Record<string, any> = { body, target_type: targetType }
    if (targetType === 'membership') params.membership_id = membershipId
    return params
  }

  const isFormValid = () => {
    if (!body.trim()) return false
    if (targetType === 'membership' && !membershipId) return false
    return true
  }

  const handlePreview = async () => {
    if (!isFormValid()) {
      toast.error('Write your message and pick an audience first')
      return
    }
    setPreviewing(true)
    try {
      const res = await axios.post('/sms_broadcasts/preview', buildParams())
      setBreakdown(res.data)
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Could not work out who this reaches')
    } finally {
      setPreviewing(false)
    }
  }

  const handleConfirmSend = async () => {
    setSending(true)
    try {
      await axios.post('/sms_broadcasts', buildParams())
      toast.success('Text message sent')
      setBody('')
      setTargetType('all_members')
      setMembershipId('')
      setBreakdown(null)
      setConfirmOpen(false)
      fetchHistory()
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to send')
    } finally {
      setSending(false)
    }
  }

  const segments = Math.max(1, Math.ceil(body.length / SEGMENT_LENGTH))
  const membershipOptions = Object.values(memberships)

  if (loading) {
    return <Box className={styles.loading}><CircularProgress size={24} /></Box>
  }

  // A disabled composer with no explanation reads as a broken page.
  if (!sendable) {
    return (
      <Card className={styles.card}>
        <CardContent>
          <Box className={styles.blocked}>
            <AlertTriangle size={18} />
            <Box>
              <Typography variant="subtitle2">{unsendableReason}</Typography>
              <Typography variant="body2" color="textSecondary">
                Texting your members requires carrier registration, which we
                handle for you — it just takes a few business days.
              </Typography>
              <button
                type="button"
                className={styles.linkButton}
                onClick={() => navigate('/settings/sms')}
              >
                Go to SMS setup
              </button>
            </Box>
          </Box>
        </CardContent>
      </Card>
    )
  }

  return (
    <Box>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        Text your members directly. Only members who agreed to receive texts
        are included, and every message carries opt-out instructions.
      </Typography>

      {balance && (
        <Card className={styles.card}>
          <CardContent className={styles.balanceCard}>
            <Box>
              <Typography variant="subtitle2">
                {balance.textsRemaining.toLocaleString()} text
                {balance.textsRemaining === 1 ? '' : 's'} left this month
                <span className={styles.unitNote}> · 160 characters each</span>
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {balance.includedPerMonth
                  ? `${balance.includedPerMonth.toLocaleString()} are included with Pro each month. `
                  : ''}
                {/* Attributed to Stitch because that is what their statement
                    will say, with the reason named so it does not read as
                    margin. */}
                After that, {balance.topUpTexts.toLocaleString()} more for
                ${(balance.topUpAmountCents / 100).toFixed(2)} to cover carrier
                fees, charged by Stitch to your card on file.
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}

      <Card className={styles.card}>
        <CardContent>
          <TextField
            label="Message"
            variant="outlined"
            fullWidth
            multiline
            minRows={3}
            value={body}
            onChange={(e) => setBody(e.target.value.slice(0, MAX_BODY_LENGTH))}
            helperText={
              `${body.length}/${MAX_BODY_LENGTH} · ${segments} segment${segments === 1 ? '' : 's'} per recipient`
            }
          />

          <Box className={styles.row}>
            <FormControl variant="outlined" className={styles.field}>
              <InputLabel>Audience</InputLabel>
              <Select
                label="Audience"
                value={targetType}
                onChange={(e) => setTargetType(e.target.value as TargetType)}
              >
                <MenuItem value="all_members">All members</MenuItem>
                <MenuItem value="recent_signups">Recent sign-ups (30 days)</MenuItem>
                <MenuItem value="membership">A specific membership</MenuItem>
              </Select>
            </FormControl>

            {targetType === 'membership' && (
              <FormControl variant="outlined" className={styles.field}>
                <InputLabel>Membership</InputLabel>
                <Select
                  label="Membership"
                  value={membershipId}
                  onChange={(e) => setMembershipId(e.target.value as string)}
                >
                  {membershipOptions.map((m: any) => (
                    <MenuItem key={m.id} value={m.id}>{m.title}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>

          {/* The breakdown, not a bare count: "3 of 40" with reasons tells a
              brewery what to fix, where "3" just looks broken. */}
          {breakdown && (
            <Box className={styles.breakdown}>
              <Typography variant="subtitle2">
                Reaches {breakdown.sendable} of {breakdown.total} members
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {breakdown.noConsent} haven't agreed to texts
                {' · '}{breakdown.unsubscribed} opted out
                {' · '}{breakdown.noPhone} have no phone number
              </Typography>
              {balance && breakdown.sendable > 0 &&
                breakdown.sendable * segments > balance.textsRemaining && (
                <Typography variant="body2" className={styles.warn}>
                  Needs {(breakdown.sendable * segments).toLocaleString()} texts,
                  you have {balance.textsRemaining.toLocaleString()} left —
                  ${(balance.topUpAmountCents / 100).toFixed(2)} will be charged
                  to top up.
                </Typography>
              )}
              {breakdown.sendable === 0 && (
                <Typography variant="body2" className={styles.warn}>
                  Nobody in this audience can be texted yet. Members agree when
                  they join, so this grows as people sign up.
                </Typography>
              )}
            </Box>
          )}

          <Box className={styles.actions}>
            <Button variant="outlined" onClick={handlePreview} disabled={previewing}>
              {previewing ? 'Checking…' : 'Check audience'}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setConfirmOpen(true)}
              // Preview is required before sending: a text cannot be recalled,
              // and the cost scales with the audience.
              disabled={!breakdown || breakdown.sendable === 0}
            >
              Send text
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Card className={styles.card}>
        <CardContent>
          <Typography variant="subtitle1" gutterBottom>Sent texts</Typography>
          {broadcasts.length === 0 ? (
            <Typography variant="body2" color="textSecondary">
              You haven't sent any texts yet.
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Message</TableCell>
                  <TableCell>Audience</TableCell>
                  <TableCell align="center">Sent to</TableCell>
                  <TableCell align="center">Delivered</TableCell>
                  <TableCell align="center">Failed</TableCell>
                  <TableCell>When</TableCell>
                  <TableCell align="center">Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {broadcasts.map((b) => (
                  <TableRow key={b.id}>
                    <TableCell className={styles.bodyCell}>{b.body}</TableCell>
                    <TableCell>{b.targetDescription}</TableCell>
                    <TableCell align="center">{b.recipientCount}</TableCell>
                    <TableCell align="center">{b.deliveredCount}</TableCell>
                    <TableCell align="center">{b.failedCount}</TableCell>
                    <TableCell>
                      {new Date(b.sentAt ?? b.createdAt).toLocaleDateString('en-US', {
                        month: 'short', day: 'numeric', year: 'numeric',
                      })}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        size="small"
                        label={b.state}
                        sx={{ backgroundColor: stateColor(b.state), color: '#fff' }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Send this text?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {breakdown?.sendable} member{breakdown?.sendable === 1 ? '' : 's'} will
            receive {segments} message segment{segments === 1 ? '' : 's'} each.
            Texts can't be recalled once sent.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmSend} variant="contained" color="primary" disabled={sending}>
            {sending ? 'Sending…' : 'Send'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

const stateColor = (state: Broadcast['state']) => {
  switch (state) {
    case 'sent': return colors.GREEN
    case 'sending': return colors.YELLOW
    case 'failed': return colors.RED
    default: return colors.GREY_3
  }
}

const useStyles = makeStyles({
  card: { marginBottom: SPACING * 2 },
  loading: { display: 'flex', justifyContent: 'center', padding: SPACING * 4 },
  row: { display: 'flex', gap: SPACING * 2, marginTop: SPACING * 2, flexWrap: 'wrap' },
  field: { minWidth: 220, flex: 1 },
  actions: { display: 'flex', gap: SPACING * 2, marginTop: SPACING * 2 },
  // A long message costs more than one, so the unit is defined where the
  // number is shown rather than left to be discovered.
  unitNote: {
    fontWeight: 400,
    color: colors.GREY_3,
  },
  balanceCard: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: SPACING * 2,
  },
  breakdown: {
    marginTop: SPACING * 2,
    padding: SPACING * 1.5,
    borderRadius: 8,
    background: colors.PALE_BLUE,
  },
  warn: { marginTop: 4, color: colors.DARK_ORANGE },
  blocked: { display: 'flex', gap: SPACING, alignItems: 'flex-start', color: colors.GREY_1 },
  bodyCell: { maxWidth: 280 },
  // A plain button, not MUI: v5's emotion styles override makeStyles, so a
  // colour set here would silently not apply to a MUI Button or Link.
  linkButton: {
    marginTop: SPACING,
    padding: 0,
    border: 'none',
    background: 'none',
    font: 'inherit',
    fontSize: 13,
    color: colors.BRIGHT_BLUE,
    cursor: 'pointer',
    textDecoration: 'underline',
  },
})

export default SmsBroadcasts
